import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { confirmDiscardIfDirty } from "./confirm-discard";
import { useOverlayShell } from "./useOverlayShell";

/**
 * Footer slot published by the open drawer.
 *
 * `undefined` means no drawer is above us - the form is standalone or inside a
 * `GlassModal`, so its actions stay where they are written. `null` means a drawer
 * is mounting and has not attached its footer element yet; the portal lands on the
 * next commit, which React flushes before paint.
 */
const DrawerFooterSlotContext = createContext<HTMLElement | null | undefined>(undefined);

/**
 * Right-edge glass drawer for setup create/edit flows. Keeps the list visible
 * on the left while a form slides in with motion and blur.
 *
 * When `dirty` is true, backdrop / Escape / close ask once before discarding.
 * Click-outside stays available (not trapped); only a lightweight confirm.
 *
 * Layout: `children` render inside the scrolling body. Action rows belong in a
 * `<GlassDrawerFooter>`, which lands on the panel's own bottom edge - outside the
 * scrollport - so the footer can never sit over the form content.
 */
export function GlassDrawer({
  open,
  onClose,
  ariaLabel,
  children,
  busy = false,
  dirty = false,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  children: ReactNode;
  busy?: boolean;
  dirty?: boolean;
  size?: "md" | "lg" | "xl";
}) {
  const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);

  function requestClose(): void {
    if (busy) return;
    if (!confirmDiscardIfDirty(dirty)) return;
    onClose();
  }

  const { panelRef } = useOverlayShell(open, busy, requestClose);

  if (!open) return null;

  return createPortal(
    <div className="glass-drawer" role="presentation">
      <button
        type="button"
        className="glass-drawer__backdrop"
        aria-label="Close panel"
        disabled={busy}
        onClick={requestClose}
      />
      <div
        ref={panelRef}
        className={`glass-drawer__panel glass-drawer__panel--${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <div className="glass-drawer__shine" aria-hidden="true" />
        <button
          type="button"
          className="glass-drawer__close"
          aria-label="Close"
          disabled={busy}
          onClick={requestClose}
        >
          ×
        </button>
        <div className="glass-drawer__body">
          <DrawerFooterSlotContext.Provider value={footerSlot}>
            {children}
          </DrawerFooterSlotContext.Provider>
        </div>
        {/* Sibling of the scrolling body, so it is a panel edge rather than a
            sticky child of the scrollport. Empty when no child claims it, and
            `:empty` keeps it out of the layout in that case. */}
        <div className="glass-drawer__footer" ref={setFooterSlot} />
      </div>
    </div>,
    document.body,
  );
}

/**
 * Action row for drawer content. Renders on the drawer panel's non-scrolling
 * bottom edge, whatever depth of the form tree writes it.
 *
 * `position: sticky; bottom: 0` inside the drawer body used to do this job. It
 * resolves against the scrollport's *content* box, so the row pinned one
 * `padding-bottom` above the visible edge and mid-scroll content passed through
 * the strip underneath it. A panel-edge sibling has no such band.
 *
 * Because the buttons leave the `<form>` subtree in the DOM, a submit button here
 * needs `form={id}` naming its form; that keeps native submit, Enter-to-submit,
 * and `required` validation intact.
 *
 * Outside a `GlassDrawer` (a modal, or a form rendered on its own) the row stays
 * where it is written.
 */
export function GlassDrawerFooter({ children }: { children: ReactNode }) {
  const slot = useContext(DrawerFooterSlotContext);
  const actions = <div className="minter__confirm-actions">{children}</div>;
  if (slot === undefined) return actions;
  if (slot === null) return null;
  return createPortal(actions, slot);
}
